/**
 * Sanitizador de datos personales (PII) antes de enviar un error a cualquier
 * destino. Es imprescindible por Habeas Data (Ley 1581): los payloads de la API
 * pueden traer identificaciones, nombres, correos, telefonos e historia clinica.
 *
 * Estrategia: recorrer recursivamente el valor y
 *  - redactar por CLAVE (cualquier campo cuyo nombre sugiera dato sensible)
 *  - redactar por VALOR (regex de cedula/email/telefono/token en strings sueltos)
 *  - truncar strings y estructuras muy grandes para no inflar el evento
 */

const CLAVES_SENSIBLES = [
  'password',
  'contrasena',
  'contrasena',
  'clave',
  'token',
  'auth_key',
  'authkey',
  'secret',
  'authorization',
  'identificacion',
  'cope_identificacion',
  'copeidentificacion',
  'documento',
  'cedula',
  'nombre',
  'nombres',
  'apellido',
  'apellidos',
  'email',
  'correo',
  'telefono',
  'celular',
  'direccion',
  'diagnostico',
  'historia',
  'observacion',
  'observaciones'
]

const REDACTADO = '[REDACTADO]'
const MAX_STRING = 500
const MAX_PROFUNDIDAD = 6
const MAX_ITEMS_ARRAY = 50

// Regex de valores sensibles frecuentes en Colombia
const RE_EMAIL = /[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}/g
// Cedula/NIT: secuencias largas de digitos (7 o mas)
const RE_DOC = /\b\d{7,}\b/g
// JWT / tokens tipo header.payload.signature
const RE_JWT = /\beyJ[a-zA-Z0-9_-]+\.[a-zA-Z0-9_-]+\.[a-zA-Z0-9_-]+/g

function claveEsSensible(clave: string): boolean {
  const c = clave.toLowerCase().replace(/[_\s-]/g, '')
  return CLAVES_SENSIBLES.some((s) => c.includes(s.replace(/[_\s-]/g, '')))
}

function sanitizarString(valor: string): string {
  let out = valor
    .replace(RE_JWT, REDACTADO)
    .replace(RE_EMAIL, REDACTADO)
    .replace(RE_DOC, REDACTADO)
  if (out.length > MAX_STRING) {
    out = out.slice(0, MAX_STRING) + `…(+${out.length - MAX_STRING} chars)`
  }
  return out
}

function sanitizar(valor: unknown, profundidad: number): unknown {
  if (valor == null) return valor
  if (typeof valor === 'string') return sanitizarString(valor)
  if (typeof valor === 'number' || typeof valor === 'boolean') return valor
  if (profundidad >= MAX_PROFUNDIDAD) return '[...]'

  if (Array.isArray(valor)) {
    const items = valor
      .slice(0, MAX_ITEMS_ARRAY)
      .map((v) => sanitizar(v, profundidad + 1))
    if (valor.length > MAX_ITEMS_ARRAY) {
      items.push(`…(+${valor.length - MAX_ITEMS_ARRAY} items)`)
    }
    return items
  }

  if (typeof valor === 'object') {
    const out: Record<string, unknown> = {}
    for (const [clave, v] of Object.entries(valor as Record<string, unknown>)) {
      out[clave] = claveEsSensible(clave)
        ? REDACTADO
        : sanitizar(v, profundidad + 1)
    }
    return out
  }

  return String(valor)
}

/**
 * Sanitiza un texto suelto (mensaje, stack) quitando PII embebida.
 */
export function sanitizarTexto(texto: string): string {
  return sanitizarString(texto)
}

/**
 * Sanitiza cualquier valor (objeto, array, string) de forma segura para envio.
 */
export function sanitizarPII<T>(valor: T): unknown {
  try {
    return sanitizar(valor, 0)
  } catch {
    return REDACTADO
  }
}
